import { ROWS_PER_PAGE_OPTIONS } from 'components-ui'
import { format } from 'date-fns'
import { useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ServiceType, SubStatus } from 'service-api/src/types/general'

export const ALL = 'all'
export type BookingsView = 'list' | 'calendar'
export type SortOrder = 'asc' | 'desc'

export interface StatusFilterOption {
  value: string
  labelKey: string
  subStatuses: SubStatus[]
}

export const STATUS_FILTER_OPTIONS: StatusFilterOption[] = [
  { value: 'canceled', labelKey: 'calendar.status.CANCELED', subStatuses: [SubStatus.CANCELLED] },
  { value: 'booked', labelKey: 'calendar.status.BOOKED', subStatuses: [SubStatus.BOOKED] },
  { value: 'checked-out', labelKey: 'calendar.status.COMPLETED', subStatuses: [SubStatus.COMPLETED] },
  { value: 'checked-in', labelKey: 'calendar.status.IN_PROGRESS', subStatuses: [SubStatus.IN_PROGRESS] },
  {
    value: 'requested',
    labelKey: 'calendar.status.REQUESTED',
    subStatuses: [SubStatus.REQUESTED, SubStatus.PENDING_SCHEDULED],
  },
  { value: 'scheduled', labelKey: 'calendar.status.SCHEDULED', subStatuses: [SubStatus.SCHEDULED] },
  { value: 'declined', labelKey: 'calendar.status.DECLINED', subStatuses: [SubStatus.DECLINED] },
  { value: 'sold', labelKey: 'calendar.status.SOLD', subStatuses: [SubStatus.SOLD] },
]

export const SERVICE_TYPE_FILTER_OPTIONS: string[] = Object.values(ServiceType)

export const DEFAULT_SORT_COLUMN = 'updatedAt'
export const DEFAULT_SORT_ORDER: SortOrder = 'desc'
export const DEFAULT_ROWS_PER_PAGE = ROWS_PER_PAGE_OPTIONS[0]

const MONTH_PATTERN = /^\d{4}-\d{2}$/

const PARAM_DEFAULTS = {
  view: 'calendar',
  status: ALL,
  serviceType: ALL,
  sortBy: DEFAULT_SORT_COLUMN,
  sortOrder: DEFAULT_SORT_ORDER,
  rows: String(DEFAULT_ROWS_PER_PAGE),
  month: '',
} as const

type ParamKey = keyof typeof PARAM_DEFAULTS

const currentMonth = () => format(new Date(), 'yyyy-MM')

const readState = (params: URLSearchParams) => {
  const status = params.get('status') ?? ALL
  const serviceType = params.get('serviceType') ?? ALL
  const sortOrder = params.get('sortOrder')
  const rows = Number(params.get('rows'))
  const month = params.get('month') ?? ''

  return {
    view: (params.get('view') === 'list' ? 'list' : 'calendar') as BookingsView,
    status: STATUS_FILTER_OPTIONS.some((o) => o.value === status) ? status : ALL,
    serviceType: SERVICE_TYPE_FILTER_OPTIONS.includes(serviceType) ? serviceType : ALL,
    sortBy: params.get('sortBy') || DEFAULT_SORT_COLUMN,
    sortOrder: (sortOrder === 'asc' || sortOrder === 'desc' ? sortOrder : DEFAULT_SORT_ORDER) as SortOrder,
    rowsPerPage: (ROWS_PER_PAGE_OPTIONS as number[]).includes(rows) ? rows : DEFAULT_ROWS_PER_PAGE,
    // yyyy-MM of the month shown on the calendar.
    month: MONTH_PATTERN.test(month) ? month : currentMonth(),
  }
}

export const useBookingsUrlState = () => {
  const [params, setParams] = useSearchParams()
  const state = useMemo(() => readState(params), [params])

  const update = useCallback(
    (changes: Partial<Record<ParamKey, string>>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev)

          Object.entries(changes).forEach(([key, value]) => {
            if (value === undefined || value === String(PARAM_DEFAULTS[key as ParamKey])) {
              next.delete(key)
            } else {
              next.set(key, value)
            }
          })

          return next
        },
        { replace: true }
      )
    },
    [setParams]
  )

  return useMemo(
    () => ({
      ...state,
      setView: (view: BookingsView) => update({ view }),
      setStatus: (status: string) => update({ status }),
      setServiceType: (serviceType: string) => update({ serviceType }),
      setSort: (sortBy: string, sortOrder: SortOrder) => update({ sortBy, sortOrder }),
      setRowsPerPage: (rows: number) => update({ rows: String(rows) }),
      setMonth: (month: string) => update({ month }),
    }),
    [state, update]
  )
}
